import { useState } from 'react';
import { Panel, Toolbar } from './Page';
import { SelectField, TextField } from './Form';
import { CardSkeleton, ErrorState } from './Feedback';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { emailIntake } from '../lib/endpoints';
import { formatDateTime } from '../lib/format';

const MATCH_TYPE_OPTIONS = [
  { value: 'Domain', label: 'Domain (e.g. acme.com)' },
  { value: 'ExactAddress', label: 'Exact address (e.g. ap@acme.com)' },
];

/**
 * PERMITTED SENDERS, ON THE SUPPLIER'S OWN CARD — the same placement principle as the AI Learning
 * and Trading Partner panels beside it: an operator who already has this vendor's card open should
 * not have to go find its raw QuickBooks vendor id and hunt for it in the generic Email Intake
 * "Approved rules" table just to say which domain or address its invoices legitimately arrive from.
 *
 * This is a thin, vendor-scoped view over that same table (GET /email-intake/rules has no
 * per-vendor filter, so this fetches the full list and filters client-side — the same list the
 * Email Intake page itself already loads in full). Adding, removing and how a rule actually gets
 * enforced (Domain rows hold a new sender for approval; ExactAddress rows auto-route) are all
 * unchanged — this panel is a different door onto the same room, not a second copy of it.
 *
 * A rule can also appear here without ever being touched by hand: binding this vendor to an
 * accepted Trading Partner connection auto-creates a Domain rule from that partner's own disclosed
 * sending domain (PartnerConnectionService.BindAsync). Nothing here distinguishes those rows from
 * ones a person typed in — they are the same kind of row, editable and removable the same way.
 */
export function VendorPermittedSendersPanel({
  qboVendorId,
  vendorName,
}: {
  qboVendorId: string;
  vendorName: string | null;
}): JSX.Element {
  const toast = useToast();
  const rulesState = useAsync((signal) => emailIntake.listRules(signal), []);

  const [busy, setBusy] = useState<string | null>(null);
  const [newMatchType, setNewMatchType] = useState<'Domain' | 'ExactAddress'>('Domain');
  const [newMatchValue, setNewMatchValue] = useState('');

  if (rulesState.loading && !rulesState.data) return <CardSkeleton />;

  if (rulesState.error && !rulesState.data) {
    return <ErrorState message="Permitted senders could not be read." detail={rulesState.error} onRetry={rulesState.reload} />;
  }

  const rules = (rulesState.data ?? []).filter((rule) => rule.qboVendorId === qboVendorId);

  const addRule = () => {
    const value = newMatchValue.trim();
    if (!value) {
      toast.error('A domain or address is required');
      return;
    }
    setBusy('add');
    emailIntake
      .upsertRule({ matchType: newMatchType, matchValue: value, qboVendorId, vendorName: vendorName ?? '' })
      .then(() => {
        toast.success('Permitted sender added');
        setNewMatchValue('');
        rulesState.reload();
      })
      .catch((caught) => toast.error('Could not add this sender', errorMessage(caught)))
      .finally(() => setBusy(null));
  };

  const removeRule = (ruleId: string) => {
    setBusy(ruleId);
    emailIntake
      .deleteRule(ruleId)
      .then(() => {
        toast.success('Permitted sender removed');
        rulesState.reload();
      })
      .catch((caught) => toast.error('Could not remove this sender', errorMessage(caught)))
      .finally(() => setBusy(null));
  };

  return (
    <div className="stack">
      <p className="field-hint">
        A Domain entry holds a new sender at that domain for a person to approve the first time it
        arrives; an Exact address auto-routes matching mail straight to this vendor. Neither depends
        on this vendor&rsquo;s invoice layout having been taught.
      </p>

      {rules.length === 0 ? (
        <p className="muted">No permitted senders yet for this vendor.</p>
      ) : (
        <ul className="plain-list">
          {rules.map((rule) => (
            <li key={rule.id} className="plain-item">
              <span style={{ flex: 1 }}>
                <strong>{rule.matchType === 'Domain' ? 'Domain' : 'Exact address'}</strong>
                {': '}
                {rule.matchValue}
                <span className="cell-sub">
                  {' '}
                  added {formatDateTime(rule.createdUtc)}
                  {rule.createdByEmail ? ` by ${rule.createdByEmail}` : ''}
                </span>
              </span>
              <button
                type="button"
                className="btn btn-link btn-danger"
                disabled={busy !== null}
                onClick={() => removeRule(rule.id)}
              >
                {busy === rule.id ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Panel title="Add a permitted sender" bodyClassName="form-grid">
        <SelectField
          label="Match type"
          value={newMatchType}
          onChange={(value) => setNewMatchType(value as 'Domain' | 'ExactAddress')}
          options={MATCH_TYPE_OPTIONS}
        />
        <TextField
          label={newMatchType === 'Domain' ? 'Domain' : 'Email address'}
          value={newMatchValue}
          onChange={setNewMatchValue}
          placeholder={newMatchType === 'Domain' ? 'acme.com' : 'ap@acme.com'}
        />
        <Toolbar>
          <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={addRule}>
            {busy === 'add' ? 'Adding…' : 'Add'}
          </button>
        </Toolbar>
      </Panel>
    </div>
  );
}
